import { createHttpService } from 'src/apis/ApiService';
import { TPaginationParams } from 'src/apis/__shared/shared.interfaces';
import { mockGuardianDocuments } from 'src/__mocks__/guardian/guardianDocuments.mock';
import isAppWithMock from 'src/utils/isAppWithMock';

export type GuardianDocument = {
  id: string;
  title: string;
  type: string;
  uploadDate: string;
  fileUrl: string;
  fileSize: number;
  category: string;
};

const useGuardianDocumentsService = () => {
  const httpAPI = createHttpService();
  const baseGuardianPath = '/guardian/documents';
  const withMockData = isAppWithMock();

  const getDocuments = (
    studentId: string,
    params?: TPaginationParams & { category?: string; type?: string }
  ): Promise<{ data: GuardianDocument[]; count: number }> => {
    if (withMockData) {
      let filteredData = [...mockGuardianDocuments];
      
      if (params?.category) {
        filteredData = filteredData.filter((d) => d.category === params.category);
      }
      if (params?.type) {
        filteredData = filteredData.filter((d) => d.type.toLowerCase() === params.type?.toLowerCase());
      }
      
      const start = params?.page && params?.limit ? (params.page - 1) * params.limit : 0;
      const end = params?.limit ? start + params.limit : filteredData.length;
      const paginatedData = filteredData.slice(start, end);
      
      return Promise.resolve({
        data: paginatedData,
        count: filteredData.length,
      });
    }

    const requestObj = {
      params: {
        studentId,
        ...(params?.page && { page: Number(params.page) }),
        ...(params?.limit && { limit: Number(params.limit) }),
        ...(params?.category && { category: params.category }),
        ...(params?.type && { type: params.type }),
      },
    };

    return httpAPI
      .get<{ data: GuardianDocument[]; count: number }>(
        baseGuardianPath,
        requestObj
      )
      .then((response) => {
        const result = response;
        if (Array.isArray(result)) {
          return { data: result, count: result.length };
        }
        return result as { data: any[]; count: number };
      })
      .catch((error) => {
        console.error('Error fetching guardian documents:', error);
        return { data: [], count: 0 };
      });
  };

  const downloadDocument = (
    studentId: string,
    documentId: string
  ): Promise<Blob> => {
    if (withMockData) {
      return Promise.resolve(new Blob());
    }

    return httpAPI
      .get<Blob>(`${baseGuardianPath}/${documentId}/download`, {
        params: { studentId },
        responseType: 'blob',
      })
      .then((response) => response as Blob)
      .catch((error) => {
        console.error('Error downloading document:', error);
        throw error;
      });
  };

  return {
    getDocuments,
    downloadDocument,
  };
};

export default useGuardianDocumentsService;
